<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" clearable hide-details />
            </template>
            <template #default>
              <v-row>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.partner_slug"
                    :items="filteredPartners"
                    :disabled="isLoading"
                    custom-type="avatar"
                    label="Partner"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.published"
                    :items="trueFalseOptions"
                    :disabled="isLoading"
                    label="Published Partners"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.source"
                    :items="sourceTypeFilterOptions"
                    :disabled="isLoading"
                    label="Source Type"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.type"
                    :items="reportTypeFilterOptions"
                    :disabled="isLoading"
                    label="Report Type"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.portalType"
                    :items="portalsFilterOptions"
                    :disabled="isLoading"
                    label="Portal Type"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.checked"
                    :items="trueFalseOptions"
                    :disabled="isLoading"
                    label="Checked"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.check_frequency"
                    :items="checkFrequencies"
                    :disabled="isLoading"
                    label="Check Frequency"
                    clearable
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>

            <template #content>
              <data-table
                :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, -1] }"
                :headers="reportHeaders"
                :items="mappedReports"
                :items-per-page="pagination.perPage"
                :page="pagination.currentPage"
                :server-items-length="pagination.total"
                disable-sort
                @pagination="$_queryHandler_onUpdatePagination"
              >
                <template #item="{ item }">
                  <td>
                    <div class="d-flex align-center">
                      <item-logo
                        :name="item.partner.name || item.partner_slug"
                        :src="item.partner.logoThumbnail"
                        class="mr-2 ml-0"
                      />
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <published-chip
                            class="flex-shrink-0 mr-2"
                            :value="item.partner.published"
                            label
                            no-text
                            small
                            v-on="on"
                          />
                        </template>
                        <span>{{ item.partner.published ? 'Published' : 'Draft' }}</span>
                      </v-tooltip>
                      <div class="max-w-120">
                        {{ item.partner.name || `${item.partner_slug} (Partner does not exist)` }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span v-if="item.deal && item.deal.name">{{ item.deal.name }}</span>
                    <v-tooltip v-else bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="error" v-bind="attrs" v-on="on">warning_amber</v-icon>
                      </template>
                      <span>Deal not found!</span>
                    </v-tooltip>
                  </td>
                  <td>{{ formatTableValue(sourceTypeFilterOptions, item.source) }}</td>
                  <td>
                    <span v-if="item.portal && item.portal.name">{{ item.portal.name }}</span>
                    <v-tooltip v-else bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="error" v-bind="attrs" v-on="on">warning_amber</v-icon>
                      </template>
                      <span>Portal not found!</span>
                    </v-tooltip>
                  </td>
                  <td>
                    <span v-if="item.portal && item.portal.type">{{
                      formatTableValue(portalsFilterOptions, item.portal.type)
                    }}</span>
                    <v-tooltip v-else bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="error" v-bind="attrs" v-on="on">warning_amber</v-icon>
                      </template>
                      <span>Portal not found!</span>
                    </v-tooltip>
                  </td>
                  <td>{{ formatTableValue(reportTypeFilterOptions, item.type) }}</td>
                  <td class="text-center">
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <v-icon
                          v-if="getReportCheckedStatus(item.checked_at, item.check_frequency)"
                          color="success"
                          v-on="on"
                          >check_circle_outline</v-icon
                        >
                        <v-icon v-else color="error" v-on="on">highlight_off</v-icon>
                      </template>
                      <div>
                        <div>Check Frequency: {{ formatTableValue(checkFrequencies, item.check_frequency) }}</div>
                        <div>Last Checked: {{ formatDate(item.checked_at, { format: 'DDMMYYYY' }) }}</div>
                      </div>
                    </v-tooltip>
                  </td>
                </template>

                <template #actions="{ item }">
                  <v-list-item :disabled="!isPermittedTo('create')" @click="onUploadModalOpen(item)">
                    <v-list-item-title>
                      <v-icon left small>backup</v-icon>
                      <span>Upload Report</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item :disabled="!isPermittedTo('create')" @click="onMarkAsCheckMenuClick(item)">
                    <v-list-item-title>
                      <v-icon left small>task_alt</v-icon>
                      <span>Mark as Checked</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    :disabled="!isPermittedTo('update')"
                    @click="$router.push({ name: 'data.reports.update', params: { id: item.id } })"
                  >
                    <v-list-item-title>
                      <v-icon left small>edit</v-icon>
                      <span>Edit Report</span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-expansion-panels>
        <v-expansion-panel>
          <v-expansion-panel-header>
            Manual CSV Upload
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col>
                If you are uploading a CSV, please make sure that you have used the
                <a
                  href="https://docs.google.com/spreadsheets/d/1ZHlo_pOE1DA0GQGuOUziVpiBjQDaXaARBxZJIH3nZhA"
                  target="_blank"
                  >CSV template</a
                >
                provided.
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <drag-and-drop-upload
                  v-model="manualCsvFile"
                  :disabled="isLoading"
                  accept=".csv, application/vnd.ms-excel"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col class="d-flex justify-end">
                <v-btn
                  :disabled="!canSaveCsv || isLoading"
                  :loading="isLoading"
                  class="ml-4"
                  color="info"
                  depressed
                  @click="onManualCsvUpload"
                >
                  <v-icon left>save</v-icon>
                  Save events
                </v-btn>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>
            Manual Entry
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row class="mt-0">
              <v-col class="d-flex align-center">
                <autocomplete-input
                  v-model="entry.partner"
                  :disabled="!isPermittedTo('create')"
                  :items="filteredPartners"
                  class="mr-4"
                  label="Partner"
                />
                <autocomplete-input
                  v-model="entry.currency"
                  :disabled="!isPermittedTo('create')"
                  :items="currencies"
                  class="mr-4"
                  label="Currency"
                />
              </v-col>
            </v-row>
            <v-row
              v-for="(event, index) in entry.events"
              :key="index"
              :class="{
                'grey lighten-4': index % 2 === 1,
              }"
              class="pb-2"
            >
              <v-col class="d-flex align-center">
                <date-picker
                  v-model="event.date"
                  :disabled="!canAddRows"
                  :error="event.date == undefined"
                  format="DDMMYYYY"
                  label="Date"
                  hide-details
                />
              </v-col>
              <v-col>
                <autocomplete-input
                  v-model="event.event_type"
                  :disabled="!canAddRows"
                  :error="event.event_type == undefined"
                  :items="eventTypes"
                  label="Event Type"
                  hide-details
                  @input="onManualEntryEventTypeChange(index)"
                />
                <autocomplete-input
                  v-model="event.deal_type"
                  :disabled="!canAddRows"
                  :error="event.deal_type == undefined"
                  :items="dealTypes"
                  :item-disabled="isDealTypeItemDisabled(event.event_type)"
                  class="mt-4"
                  label="Deal Type"
                  auto-select-first
                  cache-items
                  hide-details
                />
              </v-col>
              <v-col>
                <autocomplete-input
                  v-model="event.offer_type"
                  :disabled="!canAddRows"
                  :items="offerTypes"
                  label="Offer Type"
                  hide-details
                />
                <autocomplete-input
                  v-model="event.offer"
                  :disabled="!canAddRows || !event.offer_type"
                  :items="filteredOffers.filter(({ type }) => type === event.offer_type)"
                  class="mt-4"
                  label="Offer"
                  hide-details
                />
              </v-col>
              <v-col>
                <autocomplete-input
                  v-model="event.country"
                  :disabled="!canAddRows"
                  :items="countries"
                  label="Country"
                  hide-details
                />
                <autocomplete-input
                  v-model="event.website"
                  :disabled="!canAddRows"
                  :items="sites"
                  class="mt-4"
                  label="Website"
                  hide-details
                />
              </v-col>
              <v-col>
                <text-input v-model="event.click_id" :disabled="!canAddRows" label="Click ID" hide-details />
                <text-input
                  v-model="event.raw_amount"
                  :disabled="!canAddRows"
                  :error="event.raw_amount == undefined"
                  class="mt-4"
                  label="Amount"
                  type="number"
                  hide-details
                />
              </v-col>
              <v-col class="d-flex align-center flex-grow-0 pl-0">
                <v-btn
                  :disabled="!canAddRows || entry.events.length === 1"
                  color="error"
                  depressed
                  small
                  @click="onClickDeleteRow(index)"
                >
                  <v-icon small>close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="d-flex justify-end">
                <v-btn :disabled="!canAddRows" class="ml-4" color="primary" depressed @click="onClickAddRow">
                  <v-icon left>add</v-icon>
                  Add new row
                </v-btn>
                <v-btn :disabled="!canAddRows" class="ml-4" color="secondary" depressed @click="onClickCloneRow">
                  <v-icon left>library_add</v-icon>
                  Clone last row
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="d-flex justify-end">
                <v-btn
                  :disabled="!canSave || isLoading"
                  :loading="isLoading"
                  class="ml-4"
                  color="info"
                  depressed
                  @click="onClickSave"
                >
                  <v-icon left>save</v-icon>
                  Save events
                </v-btn>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
    <v-container>
      <v-row>
        <v-col>
          <v-card :loading="isLoading" outlined>
            <v-card-title>Pending Events (£{{ (pendingTotal / 100).toFixed(2) }})</v-card-title>
            <v-card-text>
              <data-table
                ref="pending"
                :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, 50] }"
                :headers="pendingTableHeaders"
                :items="eventLog"
                :items-per-page="eventLogPagination.perPage"
                :page="eventLogPagination.currentPage"
                :server-items-length="eventLogPagination.total"
                no-data-text="No pending events found"
                dense
                disable-sort
                small-action
                @pagination="onPendingPaginationUpdate"
              >
                <template #item.date="{ value }">
                  {{ formatDate(value, { separator: '-' }) }}
                </template>
                <template #item.partner="{ value }">
                  <router-link :to="{ name: 'partners.edit', params: { slug: value } }">{{
                    getPartnerNameFromSlug(value) || `${value} (Partner does not exist)`
                  }}</router-link>
                </template>
                <template #item.deal_type="{ value }">
                  {{ value ? getDealTypeFromSlug(value) : '-' }}
                </template>
                <template #item.event_type="{ value }">
                  {{ value ? formattedReportOption('types', value) : '-' }}
                </template>
                <template #item.click_id="{ value }">
                  <valid-click-id :value="value" />
                </template>
                <template #item.amount="{ value }">
                  {{ (value / 100).toFixed(2) }}
                </template>
                <template #item.edit="{ value }">
                  {{ (value / 100).toFixed(2) }}
                </template>

                <template #actions="{ item }">
                  <v-list-item :disabled="!isPermittedTo('update')" @click="onEditItem(item.uuid)">
                    <v-list-item-title>
                      <v-icon left small>edit</v-icon>
                      <span>Edit</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item :disabled="!isPermittedTo('delete')" @click="onDeleteItem(item.uuid)">
                    <v-list-item-title>
                      <v-icon left small>delete</v-icon>
                      <span>Delete</span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </data-table>

              <div class="mt-4">
                <v-btn
                  :disabled="!isPermittedTo('create') || !eventLog || !eventLog.length"
                  color="info"
                  depressed
                  @click="onClickUpload"
                >
                  <v-icon left>cloud_upload</v-icon>
                  Upload data
                </v-btn>
                <v-btn
                  :disabled="!isPermittedTo('create') || !eventLog || !eventLog.length"
                  class="ml-4"
                  color="error"
                  depressed
                  @click="showDeleteEventsModal = true"
                >
                  <v-icon left>delete</v-icon>
                  Delete all pending events
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
    <edit-event-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showEditModal"
      @modal-confirm="onEditConfirm"
      @modal-cancel="onEditCancel"
    >
      <v-row>
        <v-col sm="4">
          <autocomplete-input v-model="editingEvent.partner" :items="filteredPartners" label="Partner" />
        </v-col>
        <v-col sm="4">
          <autocomplete-input v-model="editingEvent.currency" :items="currencies" label="Currency" />
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="4">
          <date-picker
            v-model="editingEvent.date"
            :error="editingEvent.date == undefined"
            format="DDMMYYYY"
            label="Date"
            hide-details
          />
        </v-col>
        <v-col sm="4">
          <autocomplete-input
            v-model="editingEvent.event_type"
            :error="editingEvent.event_type == undefined"
            :items="eventTypes"
            class="mb-1"
            label="Event Type"
            hide-details
          />
        </v-col>
        <v-col sm="4">
          <autocomplete-input
            v-model="editingEvent.deal_type"
            :error="editingEvent.deal_type == undefined"
            :items="dealTypes"
            :item-disabled="isDealTypeItemDisabled(editingEvent.event_type)"
            class="mb-1"
            label="Deal Type"
            auto-select-first
            cache-items
            hide-details
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="4">
          <text-input v-model="editingEvent.click_id" class="mb-1" label="Click ID" hide-details />
        </v-col>
        <v-col sm="4">
          <autocomplete-input v-model="editingEvent.offer_type" :items="offerTypes" label="Offer Type" hide-details />
        </v-col>
        <v-col sm="4">
          <autocomplete-input
            v-model="editingEvent.offer"
            :items="filteredOffers.filter(({ type }) => type === editingEvent.offer_type)"
            class="mb-1"
            label="Offer"
            hide-details
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="4">
          <autocomplete-input
            v-model="editingEvent.country"
            :items="countries"
            class="mb-1"
            label="Country"
            hide-details
          />
        </v-col>
        <v-col sm="4">
          <autocomplete-input v-model="editingEvent.website" :items="sites" class="my-1" label="Website" hide-details />
        </v-col>
        <v-col sm="4">
          <text-input
            v-model="editingEvent.raw_amount"
            :error="editingEvent.raw_amount == undefined"
            class="my-1"
            label="Amount"
            type="number"
            hide-details
          />
        </v-col>
      </v-row>
    </edit-event-modal>

    <!-- Upload Modal -->
    <generic-modal
      v-if="showUploadModal"
      v-model="showUploadModal"
      :hide-louis="true"
      :is-disabled="isLoading"
      confirm-text="Mark as complete"
      :title="reportModalTitle"
      persistent
      @modal-confirm="onModalCompleteConfirm"
      @modal-cancel="onModalCompleteCancel"
    >
      <v-row>
        <v-col sm="6">
          <table class="report-details">
            <tr>
              <th>URL</th>
              <td>
                <a :href="reportToCheck.portal.url" target="_blank" :title="reportToCheck.portal.url">Portal</a>
              </td>
            </tr>
            <tr>
              <th>Username</th>
              <td>
                {{ reportToCheck.portal.username }}
                <copy-to-clipboard label="username" :copy-value="reportToCheck.portal.username" class="ml-2" />
              </td>
            </tr>
            <tr>
              <th>Password</th>
              <td>
                {{ reportToCheck.portal.password }}
                <copy-to-clipboard label="password" :copy-value="reportToCheck.portal.password" class="ml-2" />
              </td>
            </tr>
          </table>

          <div class="mt-2">
            <span class="font-weight-bold">Instructions</span>
            <ol>
              <li v-for="(step, index) in reportToCheck.steps" :key="index">
                {{ step }}
              </li>
            </ol>
          </div>

          <div v-if="reportToCheck.notes" class="mt-2">
            <span class="font-weight-bold">Notes</span>
            <div>{{ reportToCheck.notes }}</div>
          </div>
        </v-col>
        <v-col sm="5" offset-sm="1">
          <div>
            <item-logo :name="reportToCheck.partner.name" :src="reportToCheck.partner.logoThumbnail" class="mr-2" />{{
              reportToCheck.partner.name
            }}
          </div>

          <table class="report-details">
            <tr>
              <th>Portal</th>
              <td>{{ reportToCheck.portal.name }}</td>
            </tr>
            <tr>
              <th>Type</th>
              <td>{{ reportToCheck.type }}</td>
            </tr>
          </table>
        </v-col>
      </v-row>
      <v-row v-if="reportToCheck.source === 'csv'">
        <v-col>
          <drag-and-drop-upload v-model="modalCsvFile" accept=".csv, application/vnd.ms-excel" />
        </v-col>
      </v-row>
      <template v-if="reportToCheck.source === 'csv'" #actions>
        <v-btn
          :disabled="isLoading || !modalCsvFile"
          :dark="!(isLoading || !modalCsvFile)"
          color="blue"
          depressed
          @click="onModalCsvUpload"
        >
          <v-icon class="mr-2" small>upload</v-icon>
          <span>Upload CSV</span>
        </v-btn>
      </template>
    </generic-modal>

    <!-- Bulk Delete Modal -->
    <generic-modal
      v-model="showDeleteEventsModal"
      :is-disabled="isLoading"
      title="Delete All Pending Events"
      persistent
      @modal-confirm="onModalConfirmDeleteAllEvents"
    >
      Are you sure you want to <span class="error--text font-weight-bold">DELETE</span> all
      <span class="font-weight-bold">{{ eventLogPagination.total }}</span> pending events?
    </generic-modal>

    <fab-create v-if="isPermittedTo('create')" type="Report" @click="onClickCreate" />
  </page-loader>
</template>

<script>
// Helpers
import { clone, cloneDeep } from 'lodash-es'
import { mapGetters, mapMutations } from 'vuex'
import { getWeek, getMonth, getYear } from 'date-fns'
import { formatDate, formatTime } from '@/utils/date'
import isSet from '@/utils/is-set'
// Components
import AutocompleteInput from '@/components/forms/autocomplete-input'
import CopyToClipboard from '@/components/copy-to-clipboard'
import DataTable from '@/components/data-table'
import DatePicker from '@/components/forms/date-picker'
import DragAndDropUpload from '@/components/drag-and-drop-upload'
import EditEventModal from '@/views/data/components/edit-event-modal'
import FabCreate from '@/components/fab-create'
import FilterAccordion from '@/components/filter-accordion'
import GenericModal from '@/components/modals/generic-modal'
import ItemLogo from '@/components/item-logo'
import PageLoader from '@/components/page-loader'
import PublishedChip from '@/components/chips/published'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'
import TextInput from '@/components/forms/text-input'
import ValidClickId from '@/components/valid-click-id'
// Mixins
import DataModification from '@/mixins/data-modification'
import FilterChipsHandler from '@/mixins/filter-chips'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Services
import { deals, offers, partners, partnerLedger, partnerReports as partnerReportService } from '@/services/offers'

const entryModel = ({
  date = formatDate(new Date(), { format: 'YYYYMMDD', separator: '-' }),
  event_type = null,
  offer_type = null,
  offer = null,
  country = null,
  website = null,
  click_id = null,
  raw_amount = null,
  type = 'revenue',
} = {}) => ({
  date,
  event_type,
  offer_type,
  offer,
  country,
  website,
  click_id,
  raw_amount,
  type,
})

const resetPagination = () => ({
  currentPage: 1,
  lastPage: null,
  perPage: 20,
  total: 0,
})

const resetEntryObject = () => ({
  partner: null,
  currency: 'USD',
  events: [entryModel()],
})

export default {
  name: 'Ledger',
  components: {
    AutocompleteInput,
    CopyToClipboard,
    DataTable,
    DatePicker,
    DragAndDropUpload,
    EditEventModal,
    FabCreate,
    FilterAccordion,
    GenericModal,
    ItemLogo,
    PageLoader,
    PublishedChip,
    SearchInput,
    SelectList,
    TextInput,
    ValidClickId,
  },
  mixins: [DataModification, FilterChipsHandler, PermissionsHandler, QueryParamsHandler],
  data: () => ({
    deals: [],
    filterDrawerOpen: false,
    trueFalseOptions: [
      { key: 'Yes', value: true },
      { key: 'No', value: false },
    ],
    isLoading: false,
    reportHeaders: [
      { text: 'Partner', value: 'partner' },
      { text: 'Deal', value: 'deal.name' },
      { text: 'Source', value: 'source' },
      { text: 'Portal', value: 'portal.name' },
      { text: 'Portal Type', value: 'portal.type' },
      { text: 'Report Type', value: 'type' },
      { text: 'Checked', align: 'center', value: 'checked_at' },
      { text: '', align: 'end', value: 'actions' },
    ],
    pendingTableHeaders: [
      { text: 'Partner', value: 'partner' },
      { text: 'Date', value: 'date', width: 110 },
      { text: 'ID', value: 'click_id' },
      { text: 'Deal Type', value: 'deal_type' },
      { text: 'Event Type', value: 'event_type' },
      { text: 'Country', value: 'country' },
      { text: 'Amount (£)', align: 'end', value: 'amount' },
      { text: '', align: 'end', value: 'actions' },
    ],
    partnerReports: [],
    pendingTotal: 0,
    eventLog: [],
    offers: [],
    partners: [],
    entry: resetEntryObject(),
    modalCsvFile: null,
    manualCsvFile: null,
    eventLogPagination: resetPagination(),
    eventTypes: [
      { value: 'registration', key: 'Registration' },
      { value: 'qftd', key: 'FTD' },
      { value: 'manualRevenue', key: 'Manual Revenue' },
      { value: 'sale', key: 'Sale' },
      { value: 'basket', key: 'Basket' },
    ],
    dealTypes: [
      { value: 'CPA', key: 'CPA', event_types: ['registration', 'qftd', 'manualRevenue', 'sale', 'basket'] },
      { value: 'CPAA', key: 'CPAA', event_types: ['registration', 'qftd', 'manualRevenue'] },
      { value: 'CPL', key: 'CPL', event_types: ['registration', 'manualRevenue'] },
      { value: 'CRG', key: 'CRG', event_types: ['manualRevenue'] },
      { value: 'CPC', key: 'CPC', event_types: ['manualRevenue'] },
      { value: 'News Sales', key: 'News Sales', event_types: ['manualRevenue'] },
      { value: 'News Syndication', key: 'News Syndication', event_types: ['manualRevenue'] },
      { value: 'News Partnerships', key: 'News Partnerships', event_types: ['manualRevenue'] },
      { value: 'Programmatic', key: 'Programmatic', event_types: ['manualRevenue'] },
      { value: 'Subscriptions', key: 'Subscriptions', event_types: ['manualRevenue'] },
      { value: 'FF', key: 'FF', event_types: ['manualRevenue'] },
      { value: 'REVSHARE', key: 'Revenue Share', event_types: ['registration', 'qftd', 'manualRevenue'] },
      { value: 'news', key: 'News', event_types: ['manualRevenue'] },
      { value: 'reviews', key: 'Reviews', event_types: ['manualRevenue'] },
      { value: 'sponsored-post', key: 'Sponsored Post', event_types: ['manualRevenue'] },
    ],
    requiredFields: ['date', 'deal_type', 'raw_amount', 'type'],
    showEditModal: false,
    editingEvent: {},
    reportToCheck: null,
    showUploadModal: false,
    showDeleteEventsModal: false,
    queriesDefined: ['source', 'check_frequency', 'checked', 'type', 'partner_slug', 'published', 'portalType'],
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['formattedOption', 'getItemsForOption']),
    canSave() {
      return (
        this.canAddRows &&
        this.entry?.events?.reduce((canSave, event) => {
          const requiredDefined = Object.entries(event).every(
            ([key, value]) => !this.requiredFields.includes(key) || value != undefined
          )
          if (!requiredDefined) canSave = false
          return canSave
        }, true)
      )
    },
    canSaveCsv() {
      return this.isPermittedTo('create') && this.manualCsvFile
    },
    checkFrequencies() {
      return this.getItemsForOption('reports')?.checkFrequencies
    },
    countries() {
      return this.getItemsForOption('countries')
    },
    currencies() {
      return this.getItemsForOption('mainCurrencies')
    },
    filterChipsData() {
      return [
        {
          label: 'Search',
          model: 'query.searchTerm',
          value: this.query.searchTerm,
        },
        {
          label: 'Partner',
          model: 'query.params.partner_slug',
          options: this.filteredPartners,
          value: this.query.params.partner_slug,
        },
        {
          label: 'Published',
          model: 'query.params.published',
          options: this.trueFalseOptions,
          value: this.query.params.published,
        },
        {
          label: 'Source Type',
          model: 'query.params.source',
          options: this.sourceTypeFilterOptions,
          value: this.query.params.source,
        },
        {
          label: 'Report Type',
          model: 'query.params.type',
          options: this.reportTypeFilterOptions,
          value: this.query.params.type,
        },
        {
          label: 'Portal Type',
          model: 'query.params.portalType',
          options: this.portalsFilterOptions,
          value: this.query.params.portalType,
        },
        {
          label: 'Checked',
          model: 'query.params.checked',
          options: this.trueFalseOptions,
          value: this.query.params.checked,
        },
      ]
    },
    sites() {
      return this.getItemsForOption('sites')
    },
    offerTypes() {
      return this.getItemsForOption('offerTypes')
    },
    filteredOffers() {
      return this.offers.map(({ name, slug, type }) => ({
        key: name || slug,
        type,
        value: slug,
      }))
    },
    filteredPartners() {
      return this.partners.map(({ name, slug, logoThumbnail }) => ({
        key: name || slug,
        value: slug,
        icon: logoThumbnail,
      }))
    },
    canAddRows() {
      return !!this.entry.partner && this.isPermittedTo('create')
    },
    lastMidnightUtcDate() {
      return this.getUtcDate(new Date().setHours(0, 0, 0, 0))
    },
    mappedReports() {
      return this.partnerReports.map(({ deal_id, ...rest }) => {
        return {
          ...rest,
          deal_id,
          deal: this.deals.find(({ id }) => id === deal_id),
        }
      })
    },
    portalsFilterOptions() {
      return this.getItemsForOption('reports')?.portals
    },
    sourceTypeFilterOptions() {
      return this.getItemsForOption('reports')?.sources
    },
    reportTypeFilterOptions() {
      return this.getItemsForOption('reports')?.types
    },
    reportModalTitle() {
      return this.reportToCheck?.source === 'csv' ? 'Upload Partner Report' : 'Partner Report'
    },
    reportOptions() {
      return this.getItemsForOption('reports')
    },
  },
  async created() {
    await Promise.all([this.getOffersAndPartners(), this.getPartnerReports(), this.getEventLog(), this.getDeals()])
    this.setBeforeUnloadListener(this.onBeforeUnload)
    this.setPageLoading(false)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.entry.partner) return next()
    this.handleExitBeforeSave(next)
  },
  methods: {
    formatTime,
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    handleQueryUpdate() {
      this.getPartnerReports()
    },
    formatDate(str, opts) {
      return formatDate(str, opts)
    },
    formatTableValue(options, value) {
      const found = options.find(option => option.value === value)
      return found ? found.key : value
    },
    formattedReportOption(key, value) {
      if (!this.reportOptions) return value
      const option = this.reportOptions[key].find(option => option.value === value)
      return option ? option.key : value
    },
    isDealTypeItemDisabled(event_type) {
      return ({ event_types }) => !event_types.includes(event_type)
    },
    async getDeals() {
      this.isLoading = true
      try {
        this.deals = await deals.getActiveDeals()
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve deals',
        })
      }

      this.isLoading = false
    },
    getDealTypeFromSlug(slug) {
      if (!this.dealTypes) return slug
      const deal = this.dealTypes.find(({ value }) => value === slug)
      return deal ? deal.key : slug
    },
    getPartnerNameFromSlug(slug) {
      const found = this.filteredPartners.find(partner => partner.value === slug)
      return found ? found.key : slug
    },
    getOfferNameFromSlug(slug) {
      const found = this.filteredOffers.find(offer => offer.value === slug)
      return found ? found.key : slug
    },
    async getPartnerReports() {
      this.isLoading = true

      try {
        const { searchTerm } = this.query
        const { currentPage, perPage } = this.pagination

        const {
          data,
          pagination: { total },
        } = await partnerReportService.getFilteredReports({
          filters: this.formatQueryParams(),
          search: searchTerm,
          page: perPage === -1 ? 1 : currentPage,
          perPage: perPage === -1 ? null : perPage,
        })

        this.partnerReports = data
        this.pagination.total = total
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve partner reports',
        })
      }

      this.isLoading = false
    },
    async getEventLog() {
      this.isLoading = true

      try {
        const { currentPage: page, perPage } = this.eventLogPagination
        const params = {
          filters: { bigquery_upload_dateNotSet: true, type: 'revenue' },
          page,
          perPage,
          aggregationType: 'sum',
          aggregationProperty: 'amount',
        }

        const {
          data,
          pagination: { total },
          aggregation,
        } = await partnerLedger.getFilteredEvents(params)

        this.pendingTotal = aggregation?.value ?? 0
        this.eventLog = data
        this.eventLogPagination.total = total
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to fetch event logs',
        })
      }

      this.isLoading = false
    },
    formatQueryParams() {
      const params = clone(this.query.params)

      return Object.entries(params).reduce((acc, [key, value]) => {
        if (isSet(value)) acc[key] = value
        return acc
      }, {})
    },
    getReportCheckedStatus(date, checkFrequency) {
      if (!date) return false
      switch (checkFrequency) {
        case 'monthly':
          return getMonth(new Date(date)) === getMonth(new Date()) && getYear(new Date(date)) === getYear(new Date())
        case 'weekly':
          return getWeek(new Date(date)) === getWeek(new Date() && getYear(new Date(date)) === getYear(new Date()))
        case 'daily':
        default:
          return this.getUtcDate(new Date(date)) > this.lastMidnightUtcDate
      }
    },
    getUtcDate(date) {
      const utcString = new Date(date).toUTCString()
      return new Date(utcString)
    },
    onEventTypeChange(index) {
      this.entry.events[index].deal_type = null
    },
    onEditItem(uuid) {
      this.editingEvent = cloneDeep(this.eventLog.find(event => event.uuid === uuid))
      this.showEditModal = true
    },
    async onDeleteItem(uuid) {
      this.isLoading = true
      try {
        await partnerLedger.deleteEvent(uuid)
        await this.getEventLog()
        this.$notify({
          type: 'success',
          text: 'Successfully deleted event',
        })
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete event',
        })
      }
      this.isLoading = false
    },
    async onClickUpload() {
      this.isLoading = true

      try {
        await partnerLedger.confirmEvents('revenue')
        await this.getEventLog()
        this.$notify({
          type: 'success',
          text: 'Events uploaded',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to upload events',
        })
      }

      this.isLoading = false
    },
    onClickAddRow() {
      this.entry.events.push(entryModel())
    },
    onClickCloneRow() {
      const lastItem = cloneDeep(this.entry.events[this.entry.events.length - 1])
      this.entry.events.push(lastItem)
    },
    onClickCreate() {
      this.$router.push({ name: 'data.reports.create' })
    },
    onClickDeleteRow(index) {
      const updatedEntries = this.entry.events.slice(0, index).concat(this.entry.events.slice(index + 1))
      this.$set(this.entry, 'events', updatedEntries)
    },
    async onClickSave() {
      this.isLoading = false

      try {
        await partnerLedger.uploadManual(this.entry)
        await this.getEventLog()
        this.$notify({
          type: 'success',
          text: 'Successfully saved manual revenue entry',
        })
        this.entry = resetEntryObject()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save events',
        })
      }

      this.isLoading = false
    },
    onManualEntryEventTypeChange(index) {
      // Prevent entry using a disabled deal_type option when changing event_type.
      this.$set(this.entry.events[index], 'deal_type', undefined)
    },
    async onManualCsvUpload() {
      this.isLoading = false

      try {
        await partnerLedger.uploadManualCSV({
          partner: this.entry.partner,
          currency: this.entry.currency,
          events: this.manualCsvFile,
        })
        await this.getEventLog()
        this.entry = resetEntryObject()

        this.$notify({
          type: 'success',
          text: 'Successfully uploaded events CSV',
        })

        this.manualCsvFile = null
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save events',
        })
      }

      this.isLoading = false
    },
    onBeforeUnload(event) {
      if (!this.entry.partner) return next()
      this.confirmUnload(event)
    },
    async getOffersAndPartners() {
      this.isLoading = true

      try {
        const { data: offerData } = await offers.getAllOffers({ compact: true })
        this.offers = offerData
        const { data: partnerData } = await partners.getAllPartners({
          compact: true,
          filters: { archivedContains: [true, false] },
        })
        this.partners = partnerData
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Offers & Partners',
        })
      }

      this.isLoading = false
    },
    async onEditConfirm() {
      this.isLoading = true

      try {
        await partnerLedger.editEvent(this.editingEvent.uuid, this.editingEvent)
        await this.getEventLog()

        this.showEditModal = false
        this.editingEvent = {}

        this.$notify({
          type: 'success',
          text: 'Event Updated',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save event',
        })
      }

      this.isLoading = false
    },
    onEditCancel() {
      this.showEditModal = false
      this.editingEvent = {}
    },
    onMarkAsCheckMenuClick(reportData) {
      this.reportToCheck = reportData
      this.onModalCompleteConfirm()
    },
    onUploadModalOpen(reportData) {
      this.reportToCheck = reportData
      this.showUploadModal = true
    },
    async onModalConfirmDeleteAllEvents() {
      try {
        await partnerLedger.deleteFilteredEvents({
          filters: {
            type: 'revenue',
            bigquery_upload_dateNotSet: true,
          },
        })
        this.showDeleteEventsModal = false
        this.$notify({
          type: 'success',
          text: 'Successully removed pending events',
        })
        await this.getEventLog()
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete pending events',
        })
        this.showDeleteEventsModal = false
      }
    },
    async onModalCompleteConfirm() {
      this.isLoading = true

      try {
        const data = { ...this.reportToCheck, checked_at: new Date().toUTCString() }
        await partnerReportService.updateReport(this.reportToCheck.id, data)
        this.$notify({
          type: 'success',
          text: 'Marked as Complete',
        })
        this.reportToCheck = null
        this.showUploadModal = false
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to mark Partner Report as complete',
        })
      }
      this.isLoading = false

      await this.getPartnerReports()
    },
    async onModalCsvUpload() {
      this.isLoading = true

      try {
        await partnerLedger.uploadCSV(this.reportToCheck.id, this.modalCsvFile)
        this.$notify({
          type: 'success',
          text: 'Successfully uploaded CSV',
        })
        this.showUploadModal = false
        this.modalCsvFile = null

        const data = { ...this.reportToCheck, checked_at: new Date().toUTCString() }
        await partnerReportService.updateReport(this.reportToCheck.id, data)

        await Promise.all([this.getPartnerReports(), this.getEventLog()])
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to upload CSV',
        })
      }
      this.isLoading = false
    },
    onModalCompleteCancel() {
      this.modalCsvFile = null
      this.reportToCheck = null
      this.showUploadModal = false
    },
    onPendingPaginationUpdate({ page, itemsPerPage }) {
      if (this.eventLogPagination.perPage !== itemsPerPage) {
        this.eventLogPagination.currentPage = 1
      } else {
        this.eventLogPagination.currentPage = page
      }
      this.eventLogPagination.perPage = itemsPerPage

      this.getEventLog()
    },
  },
}
</script>

<style lang="scss" scoped>
.report-details {
  width: 100%;

  th {
    text-align: left;
  }
}

.relative {
  position: relative;
}

.table-results {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  pointer-events: none;
}

.max-w-120 {
  max-width: 120px;
}
</style>
